<template>
    <div class="page">
        <header class="page__header">
            <h1 class="page__title">Choix bénéficiaire Carte Vitale</h1>
            <div class="page__meta">
                Bénéficiaire actif :
                <strong>{{ getIndexBenefActif ?? '—' }}</strong>
            </div>
            <button class="btn btn--ghost" @click="lancerAdr">Lancer ADR</button>
        </header>

        <div class="grid grid--3">
            <!-- Colonne gauche : Liste bénéficiaires -->
            <section class="card">
                <h2 class="card__title">Bénéficiaires</h2>
                <!-- ⏳ Loader tant que la CV n'est pas encore arrivée -->
                <div v-if="isLoadingBenef" class="list-loading">
                    <!-- TODO sans cet balise le loading se fait à l'infini'-->
                    <h1 style="display:none">
                        benefList.length = {{ benefList.length }}
                    </h1>
                    <div class="spinner" aria-label="Chargement des bénéficiaires" role="status"></div>
                    <p class="muted">Chargement des bénéficiaires…</p>
                </div>
                <!-- ✅ Liste quand c'est chargé -->
                <div v-else>
                    <div class="list" role="listbox" aria-label="Liste des bénéficiaires">
                        <article v-for="(b, idx) in benefList"
                                 :key="b.id"
                                 :id="'benef' + idx"
                                 class="list__item"
                                 role="option"
                                 :aria-selected="getIndexBenefActif === idx"
                                 :class="{ 'is-selected': getIndexBenefActif === idx }"
                                 tabindex="0"
                                 @click="selectionnerBeneficiaire(idx)"
                                 @keydown.enter.prevent="selectionnerBeneficiaire(idx)"
                                 @keydown.space.prevent="selectionnerBeneficiaire(idx)">
                            <div class="avatar">{{ getInitiales(b.nom, b.prenom) }}</div>
                            <div class="list__content">
                                <div class="list__line">
                                    <span class="list__name">{{ b.nom }} {{ b.prenom }}</span>
                                    <span class="list__meta"> ({{ b.dateNaissance.substring(6,8) }}/{{ b.dateNaissance.substring(4,6) }}/{{ b.dateNaissance.substring(0,4) }})</span>
                                </div>
                                <!--<div class="list__hint">index : {{ idx }} • source : {{ b.source }}</div>-->
                            </div>
                        </article>
                    </div>

                    <!-- 📨 Aucun résultat après chargement -->
                    <p v-if="!benefList.length" class="empty">Aucune donnée carte Vitale chargée.</p>
                </div>
            </section>

            <!-- Colonne centre : ADR intégré -->
            <section class="card">
                <h2 class="card__title">Acquisition des droits</h2>
                <template v-if="selectedBenef">
                    <PanneauADR :resultat="adrResultat"
                                :statut="adrStatut"
                                :erreur="adrErreur"
                                :medecin-i-m-t="medecinIMT" />
                </template>
                <template v-else>
                    <div class="placeholder">
                        <p>Sélectionnez un bénéficiaire pour afficher l’ADR.</p>
                    </div>
                </template>
            </section>

            <!-- Colonne droite : Options -->
            <section class="card">
                <h2 class="card__title">Actions & options</h2>

                <div class="form">
                    <div class="form__row">
                        <label class="chk">
                            <input type="checkbox" />
                            <span>Création patient</span>
                        </label>
                        <label class="chk">
                            <input type="checkbox" />
                            <span>Mise à jour patient</span>
                        </label>
                    </div>

                    <button class="btn btn--soft" @click="corrigerSiecleNaissance">
                        Modifier le siècle de naissance si erroné
                    </button>

                    <div class="kv">
                        <div class="kv__key">Numéro de série</div>
                        <div class="kv__val">
                            123456789 <button class="chip" type="button">INSC-C</button>
                        </div>
                    </div>

                    <fieldset class="fieldset">
                        <legend>S’agit-il d’un accident ?</legend>
                        <label class="radio">
                            <input type="radio" name="accident" value="oui" v-model="accident" />
                            <span>Oui</span>
                        </label>
                        <label class="radio">
                            <input type="radio" name="accident" value="non" v-model="accident" />
                            <span>Non</span>
                        </label>
                    </fieldset>

                    <div class="btn-stack">
                        <button class="btn" type="button">Forcer les droits ALD</button>
                        <button class="btn" type="button">Forcer les services AMO</button>
                    </div>

                    <div class="fieldset">
                        <div class="chk">
                            <input type="checkbox" id="acs" />
                            <label for="acs">Contrat ACS TP SOCIAL AMO (sur attestation)</label>
                        </div>
                    </div>

                    <div class="dates">
                        <p>{{ dateAujourdhui }}</p>
                        <p>Date du jour : <strong>{{ dateAujourdhui }}</strong></p>
                    </div>

                    <p class="banner banner--warn">TEST</p>

                    <div class="form__actions">
                        <button class="btn btn--primary" @click="redirigerVersFSE">OK</button>
                        <button class="btn btn--outline" type="button">Annuler</button>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
    import { defineComponent } from 'vue';
    import config from '../config';
    import RequestManager from '../script/request';
    import PanneauADR from './PanneauADR.vue';
    import { mapGetters, mapActions } from 'vuex';

    export default defineComponent({
        components: { PanneauADR },
        data() {
            return {
                carteVitaleInData: null,
                accident: 'non'
            };
        },
        computed: {
            ...mapGetters('SSV', ['getIndexBenefActif']),
            // Getter du store : on mémorise localement pour savoir si "chargé" ou pas
            carteVitale() {
                const cv = this.$store.getters['SSV/getCarteVitale'];
                this.carteVitaleInData = cv; // null/undefined => pas encore chargé
                console.log("carteVitale = ", this.carteVitaleInData);
                return cv;
            },
            enumCarteVitale() {
                return this.$store.getters['SSV/getStructures_publiques_cartevitale'];
            },
            dateAujourdhui() {
                const options = { year: 'numeric', month: 'long', day: 'numeric' };
                return new Intl.DateTimeFormat('fr-FR', options).format(new Date());
            },
            // ⏳ Loading tant qu'AUCUNE donnee carte vitale n'est encore arrivee.
            // Des qu'une carte est recue (objet non vide), on arrete le spinner :
            // si aucun beneficiaire n'en est extrait, le message "Aucune donnee"
            // s'affiche (au lieu de tourner indefiniment).
            isLoadingBenef() {
                const cv = this.$store.getters['SSV/getCarteVitale'];
                return cv === null
                    || typeof cv === 'undefined'
                    || (typeof cv === 'object' && Object.keys(cv).length === 0);
            },
            // ✅ Liste uniquement à partir de la carte Vitale (plus de fallback "lesBeneficiares")
            benefList() {
                const list = [];
                if (this.carteVitale?.benef?.length) {
                    const e = this.enumCarteVitale;
                    for (let i = 0; i < this.carteVitale.benef.length; i++) {
                        const b = this.carteVitale.benef[i];
                        if (b?.FieldsCount > 0) {
                            const nom = b.Fields?.[e.VITALE_NOM_USUEL] ?? '';
                            const prenom = b.Fields?.[e.VITALE_PRENOM] ?? '';
                            const rawDate = b.Fields?.[e.VITALE_DATE_NAISSANCE] ?? '';
                            list.push(this.normalizeBenefFromCV(b, i, nom, prenom, rawDate));
                        }
                    }
                }
                return list;
            },
            selectedBenef() {
                const i = this.getIndexBenefActif;
                if (i == null || i < 0) return null;
                return this.benefList[i] ?? null;
            },
            // Etat ADR (resultat + statut + erreur) alimente par le store apres l'appel ADR.
            adrResultat() {
                return this.$store.getters['ChoixBenecStore/getDonneeResponseADR'] || "";
            },
            adrStatut() {
                return this.$store.getters['ChoixBenecStore/getStatutADR'] || "";
            },
            adrErreur() {
                return this.$store.getters['ChoixBenecStore/getErreurADR'] || "";
            },
            medecinIMT() {
                return this.$store.getters['ChoixBenecStore/getMedecinIMT'];
            }
        },
        methods: {
            ...mapActions('SSV', ['setIndexBenefActif']),
            async setIndexBenefActif(benefActif) {
                this.$store.dispatch("SSV/setIndexBenefActif", benefActif);
                await this.$store.dispatch("ChoixBenecStore/updateChoixBenec", benefActif);
                this.lancerAdr();
            },
            normalizeBenefFromCV(b, idx, nom, prenom, rawDate) {
                return {
                    id: `cv-${idx}`,
                    nom: nom || '—',
                    prenom: prenom || '—',
                    dateNaissance: this.formatDateMaybeRaw(rawDate),
                    source: 'carte vitale',
                    raw: b
                };
            },
            selectionnerBeneficiaire(index) {
                this.setIndexBenefActif(this.getIndexBenefActif === index ? null : index);
            },
            lancerAdr() {
                if (this.getIndexBenefActif != null) {
                    const url = `${config.urlBackEnd}Assertion/getParametersForComminInit/${this.getIndexBenefActif}`;
                    console.log(url);
                    RequestManager.get(url).then(response => {
                        RequestManager.EnvoyerMessageWebView({ action: 'loadXML', parameters: response });
                    });
                } else {
                    alert('Avant de faire appel à ADR, veuillez choisir un bénéficiaire.');
                }
            },

            corrigerSiecleNaissance() {
                alert('Vérification/Correction du siècle de naissance lancée.');
            },
            redirigerVersFSE() {
                // Memorise le beneficiaire vitale selectionne comme patient de la FSE,
                // puis ouvre NewFSE (meme principe que "+ Creer FSE" depuis la liste Patients).
                const b = this.selectedBenef;
                this.$store.dispatch('FSEHandler/setPatientCourant', b ? {
                    Nom: b.nom,
                    Prenom: b.prenom,
                    DateNais: b.dateNaissance,
                    source: 'carte vitale',
                    raw: b.raw,
                } : null);
                this.$router.push({ name: 'NewFSE' });
            },
            getInitiales(nom = '', prenom = '') {
                const n = (nom || '').trim();
                const p = (prenom || '').trim();
                return (n[0] || '?').toUpperCase() + (p[0] || '?').toUpperCase();
            },
            formatDateMaybeRaw(rawDate = '') {
                if (/^\d{8}$/.test(rawDate)) {
                    const an = rawDate.slice(0, 4);
                    const mo = rawDate.slice(4, 6);
                    const jo = rawDate.slice(6, 8);
                    return `${jo}/${mo}/${an}`;
                }
                return rawDate || '—';
            }
        }
    });
</script>

<style scoped>
    /* Variables */
    :root {
        --bg: #f7f9fc;
        --card: #ffffff;
        --text: #1f2937;
        --muted: #6b7280;
        --primary: #004aad;
        --primary-600: #003d90;
        --ring: rgba(0, 74, 173, 0.35);
        --warn: #d97706;
        --shadow: 0 8px 24px rgba(16, 24, 40, 0.08);
        --radius: 16px;
    }

    /* Layout */
    .page {
        min-height: 100%;
        background: var(--bg);
        padding: 24px;
        color: var(--text);
        font-family: system-ui, sans-serif;
    }

    .page__header {
        display: flex;
        align-items: center;
        gap: 12px;
        justify-content: space-between;
        margin-bottom: 20px;
        flex-wrap: wrap;
    }

    .page__title {
        font-size: 22px;
        font-weight: 700;
    }

    .page__meta {
        color: var(--muted);
        font-size: 14px;
    }

    .grid--3 {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 16px;
    }

    /* Cards */
    .card {
        background: var(--card);
        border-radius: var(--radius);
        box-shadow: var(--shadow);
        padding: 16px;
    }

    .card__title {
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 12px;
    }

    /* List */
    .list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-height: 460px;
        overflow: auto;
    }

    .list__item {
        display: flex;
        gap: 12px;
        align-items: center;
        background: #f4edff;
        border-radius: 12px;
        padding: 10px 12px;
        transition: background 0.2s;
    }

        .list__item.is-selected {
            background: var(--primary);
            color: #fff;
        }

    .avatar {
        width: 40px;
        height: 40px;
        border: 2px solid #0001;
        border-radius: 50%;
        display: grid;
        place-items: center;
        font-weight: bold;
    }

    .list__content {
        display: grid;
        gap: 2px;
    }

    .list__line {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }

    .list__name {
        font-weight: 600;
    }

    .list__hint {
        font-size: 12px;
        color: var(--muted);
    }

    /* Loader */
    .list-loading {
        min-height: 180px;
        display: grid;
        place-items: center;
        gap: 8px;
        text-align: center;
    }

    .spinner {
        width: 36px;
        height: 36px;
        border: 3px solid #e5e7eb;
        border-top-color: var(--primary);
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }

    .muted {
        color: var(--muted);
        font-size: 14px;
    }

    /* Form & buttons */
    .form {
        display: grid;
        gap: 12px;
    }

    .form__row {
        display: flex;
        gap: 12px;
    }

    .chk, .radio {
        display: flex;
        gap: 6px;
    }

    .fieldset {
        border: 1px dashed #ccc;
        padding: 8px;
        border-radius: 8px;
        display: flex;
        gap: 12px;
    }

    .btn {
        border: none;
        padding: 8px 12px;
        border-radius: 8px;
        cursor: pointer;
    }

    .btn--primary {
        background: var(--primary);
        color: #fff;
    }

    .btn--outline {
        border: 1px solid #ccc;
        background: none;
    }

    .btn--soft {
        background: #fdf2e9;
    }

    .btn--ghost {
        background: none;
        color: var(--primary);
    }

    .btn-stack {
        display: grid;
        gap: 8px;
    }

    .chip {
        border: 1px solid #ccc;
        border-radius: 999px;
        padding: 2px 8px;
    }

    /* Placeholder */
    .placeholder {
        border: 1px dashed #ccc;
        padding: 16px;
        border-radius: 8px;
        text-align: center;
        color: var(--muted);
    }

    .empty {
        color: var(--muted);
        margin-top: 8px;
    }
</style>
